import React, { useState, useEffect, useRef } from 'react';
import Sparkles from '../../../macadamia-core/icons/Sparkles';

// CSS pour les différents états de l'input
const GlowCSS = `
  .search-glow-wrapper {
    position: relative;
    border-radius: 8px;
    overflow: visible;
  }

  /* Effet de bordure interne (pour la recherche) */
  .search-glow-border {
    position: absolute;
    inset: -2px;
    border-radius: 10px;
    z-index: 0;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    background: linear-gradient(90deg, 
      #FF6778, #FFBA71, #8D99FF, #F5B9EA, #FF6778
    );
    background-size: 300% 100%;
    animation: gradientFlow 8s ease infinite;
  }

  /* Effet de flou de fond (ombre portée) */
  .search-glow-blur {
    position: absolute;
    inset: 0px;
    border-radius: 0px;
    z-index: 1;
    opacity: 0;
    filter: blur(6px);
    transition: opacity 0.4s ease-in-out;
    background: linear-gradient(90deg, 
      #FF6778, #FFBA71, #8D99FF, #F5B9EA, #FF6778
    );
    background-size: 300% 100%;
    animation: gradientFlow 8s ease infinite;
  }

  @keyframes gradientFlow {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  /* État focused : activation du flou de fond */
  .search-glow-wrapper-focused .search-glow-blur {
    opacity: 0.7;
  }

  /* État avec texte : intensification légère du flou */
  .search-glow-wrapper-with-text .search-glow-blur {
    opacity: 0.9;
  }

  /* État recherche : activation de la bordure interne */
  .search-glow-wrapper-searching .search-glow-border {
    opacity: 1;
  }

  .search-glow-inner {
    position: relative;
    border-radius: 8px;
    z-index: 1;
    transition: all 0.3s ease;
  }
`;

interface SearchInputProps {
  onSearch: (searchTerm: string) => void;
}

const SearchInput: React.FC<SearchInputProps> = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Injecter le CSS dans le document
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = GlowCSS;
    document.head.appendChild(styleElement);

    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  // Nettoyer les timeouts lors du démontage
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Fonction pour démarrer la recherche
  const startSearch = () => {
    if (!searchTerm.trim() || isSearching) return;

    setIsSearching(true);
    setIsFocused(false); // On sort du focus pour entrer en recherche
    onSearch(searchTerm); // Envoyer le terme de recherche au parent

    // onSearch(searchTerm); // Déjà appelé ci-dessus

    // Timeout uniquement pour l'effet visuel "searching" (glow), pas pour vider le champ !
    searchTimeoutRef.current = setTimeout(() => {
      setIsSearching(false);
      // On NE VIDE PAS searchTerm ni onSearch('') ici !
    }, 1500);
  };

  // Gérer le focus
  const handleFocus = () => {
    setIsFocused(true);
  };

  // Gérer la perte du focus
  const handleBlur = () => {
    setIsFocused(false);
    if (searchTerm.trim()) {
      startSearch();
    }
  };

  // Gérer la pression de la touche Entrée
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur(); // Force la perte du focus
      startSearch();
    }
  };

  // Déterminer les classes CSS pour l'état de l'input
  const getWrapperClasses = () => {
    const classes = ['search-glow-wrapper'];

    if (isSearching) {
      classes.push('search-glow-wrapper-searching');
    } else if (searchTerm.trim()) {
      classes.push('search-glow-wrapper-with-text');
    } else if (isFocused) {
      classes.push('search-glow-wrapper-focused');
    }

    return classes.join(' ');
  };

  return (
    <div className={getWrapperClasses()}>
      {/* Effet de bordure interne (recherche) */}
      <div className="search-glow-border"></div>

      {/* Effet de flou de fond (focus et texte) */}
      <div className="search-glow-blur"></div>

      {/* Conteneur interne */}
      <div className="search-glow-inner relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Sparkles
            width="20px"
            height="20px"
            stroke="rgb(156 163 175)"
          />
        </div>
        <input
          type="text"
          placeholder="Que recherchez-vous ? Décrivez les décisions qui vous intéressent"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 
                     bg-white dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100
                     focus:ring-2 focus:ring-mcdm-primary-300 dark:focus:ring-mcdm-third-200
                     focus:border-mcdm-primary-300 outline-none transition-all duration-200
                     placeholder-gray-400 dark:placeholder-gray-500 text-sm"
        />
      </div>
    </div>
  );
};

export default SearchInput;